import { useState, useCallback, useMemo } from "react";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { RequirePermission } from "@/components/RequirePermission";
import { useBills, useApproveBill, useMarkBillPaid, useVoidBill } from "@/hooks/useBills";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { usePagination } from "@/hooks/usePagination";
import { usePermissions } from "@/hooks/usePermissions";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { APAgingReport } from "@/components/controller/APAgingReport";
import { DataTable } from "@/components/shared/DataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { MoreHorizontal, CheckCircle, DollarSign, XCircle, AlertTriangle, Printer } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import type { Bill } from "@/hooks/useBills";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { exportPdf } from "@/lib/export";
import { BillPrintTemplate } from "@/components/shared/print-templates/BillPrintTemplate";

const fmt = (n: number) =>
  `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

// ── Mark as Paid Dialog ──────────────────────────────────────────────────
function MarkPaidDialog({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const [ref, setRef] = useState("");
  const [paidDate, setPaidDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [paymentMethod, setPaymentMethod] = useState("ach");
  const [amount, setAmount] = useState("");
  const markPaid = useMarkBillPaid();

  // Reset amount when bill changes
  const billTotal = bill?.total ?? 0;
  const effectiveAmount = amount === "" ? billTotal : Number(amount);
  const isPartial = effectiveAmount > 0 && effectiveAmount < billTotal;

  const handleSubmit = async () => {
    if (!bill) return;
    try {
      await markPaid.mutateAsync({
        billId: bill.id,
        paymentReference: ref.trim() || undefined,
        paidAt: paidDate ? new Date(paidDate).toISOString() : undefined,
        paymentMethod,
        amountPaid: effectiveAmount,
      });
      toast.success(`Bill ${bill.bill_number} ${isPartial ? "partially " : ""}marked as paid`);
      onClose();
      setRef("");
      setAmount("");
      setPaymentMethod("ach");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to mark bill as paid");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Bill <span className="font-mono font-semibold">{bill?.bill_number}</span> — Total {fmt(billTotal)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="paid-date">Payment Date</Label>
            <Input
              id="paid-date"
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pay-method">Payment Method</Label>
            <select
              id="pay-method"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="ach">ACH</option>
              <option value="check">Check</option>
              <option value="wire">Wire Transfer</option>
              <option value="credit_card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pay-amount">Amount</Label>
            <Input
              id="pay-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={billTotal}
              placeholder={fmt(billTotal)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {isPartial && (
              <p className="text-xs text-amber-600">Partial payment — bill will be marked as partially paid.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref">Payment Reference (check #, confirmation, etc.)</Label>
            <Input
              id="ref"
              placeholder="Optional"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={markPaid.isPending || effectiveAmount <= 0}>
            <DollarSign className="h-4 w-4 mr-1" />
            {markPaid.isPending ? "Saving…" : isPartial ? "Record Partial Payment" : "Confirm Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Void Dialog ───────────────────────────────────────────────────────────
function VoidDialog({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const voidBill = useVoidBill();

  const handleVoid = async () => {
    if (!bill || !reason.trim()) return;
    try {
      await voidBill.mutateAsync({ billId: bill.id, reason: reason.trim() });
      toast.success(`Bill ${bill.bill_number} voided`);
      onClose();
      setReason("");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to void bill");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" /> Void Bill
          </DialogTitle>
          <DialogDescription>
            Void bill <span className="font-mono font-semibold">{bill?.bill_number}</span>. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="void-reason">Reason for voiding <span className="text-destructive">*</span></Label>
          <Textarea
            id="void-reason"
            placeholder="Describe why this bill is being voided…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!reason.trim() || voidBill.isPending}
            onClick={handleVoid}
          >
            <XCircle className="h-4 w-4 mr-1" />
            {voidBill.isPending ? "Voiding…" : "Void Bill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Approve Confirm ───────────────────────────────────────────────────────
function ApproveConfirm({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const approve = useApproveBill();

  const handleApprove = async () => {
    if (!bill) return;
    try {
      await approve.mutateAsync(bill.id);
      toast.success(`Bill ${bill.bill_number} approved`);
      onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to approve bill");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve Bill?</AlertDialogTitle>
          <AlertDialogDescription>
            Approve bill <span className="font-mono font-semibold">{bill?.bill_number}</span> for {fmt(bill?.total ?? 0)}?
            This will mark it as approved and ready for payment.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleApprove} disabled={approve.isPending}>
            <CheckCircle className="h-4 w-4 mr-1" />
            {approve.isPending ? "Approving…" : "Approve"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Print helper — fetches line items then opens print window ─────────────
async function printBill(bill: Bill, companyLogoUrl?: string | null, companyName?: string) {
  const { data: lines } = await supabase
    .from("bill_lines")
    .select("description, quantity, unit_price, line_total, sov_line_id, sov_lines(description)")
    .eq("bill_id", bill.id)
    .order("created_at");

  // Consolidate line items by SOV line
  const rawLines = lines ?? [];
  const sovGroups: Record<string, { description: string; units: string[]; quantity: number; unit_price: number; line_total: number }> = {};

  for (const l of rawLines as any[]) {
    const sovId = l.sov_line_id ?? l.id;
    const sovDesc = l.sov_lines?.description ?? l.description;

    // Extract unit number from description (pattern: "... — Unit XXX" or "Unit XXX")
    const unitMatch = l.description?.match(/(?:Unit|unit|#)\s*(\S+)/);
    const unitNum = unitMatch?.[1];

    if (!sovGroups[sovId]) {
      sovGroups[sovId] = {
        description: sovDesc,
        units: unitNum ? [unitNum] : [],
        quantity: Number(l.quantity),
        unit_price: Number(l.unit_price),
        line_total: Number(l.line_total),
      };
    } else {
      if (unitNum && !sovGroups[sovId].units.includes(unitNum)) {
        sovGroups[sovId].units.push(unitNum);
      }
      sovGroups[sovId].quantity += Number(l.quantity);
      sovGroups[sovId].line_total += Number(l.line_total);
    }
  }

  const lineItems = Object.values(sovGroups).map((g) => ({
    description: g.units.length > 0
      ? `${g.description} · Unit${g.units.length > 1 ? "s" : ""} ${g.units.join(", ")}`
      : g.description,
    quantity: g.quantity,
    unit_price: g.quantity > 0 ? g.line_total / g.quantity : 0,
    line_total: g.line_total,
  }));

  exportPdf(
    <BillPrintTemplate
      data={{
        bill_number: bill.bill_number,
        bill_date: bill.bill_date,
        due_date: bill.due_date,
        status: bill.status,
        subtotal: bill.subtotal,
        retainage_amount: bill.retainage_amount,
        retainage_percent: bill.retainage_percent,
        total: bill.total,
        vendor_name: bill.vendors?.name,
        project_name: bill.projects?.name,
        project_number: bill.projects?.project_number,
        company_name: companyName,
        company_logo_url: companyLogoUrl,
        description: bill.description,
        payment_terms: (bill as any).payment_terms,
        line_items: lineItems,
      }}
    />,
    `Bill ${bill.bill_number}`
  );
}

// ── Bill Actions Dropdown ─────────────────────────────────────────────────
function BillActions({
  bill,
  onApprove,
  onMarkPaid,
  onVoid,
  companyLogoUrl,
  companyName,
}: {
  bill: Bill;
  onApprove: (b: Bill) => void;
  onMarkPaid: (b: Bill) => void;
  onVoid: (b: Bill) => void;
  companyLogoUrl?: string | null;
  companyName?: string;
}) {
  const { can, isAdmin } = usePermissions();
  const canApprove = isAdmin || can("approve_bills");
  const canPay = isAdmin || can("approve_payments");

  const isVoided = bill.status === "voided";
  const isPaid = bill.status === "paid";
  const canApproveAction = canApprove && (bill.status === "draft" || bill.status === "pending");
  const canMarkPaidAction = canPay && (bill.status === "approved" || bill.status === "partially_paid");
  const canVoid = isAdmin || can("void_bills");
  const canVoidAction = canVoid && !isVoided && !isPaid;

  if (!canApproveAction && !canMarkPaidAction && !canVoidAction) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        title="Print / PDF"
        onClick={() => printBill(bill, companyLogoUrl, companyName)}
      >
        <Printer className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={() => printBill(bill, companyLogoUrl, companyName)}>
          <Printer className="h-4 w-4 mr-2" /> Print / PDF
        </DropdownMenuItem>
        {canApproveAction && (
          <DropdownMenuItem onClick={() => onApprove(bill)}>
            <CheckCircle className="h-4 w-4 mr-2 text-green-600" /> Approve
          </DropdownMenuItem>
        )}
        {canMarkPaidAction && (
          <DropdownMenuItem onClick={() => onMarkPaid(bill)}>
            <DollarSign className="h-4 w-4 mr-2 text-blue-600" /> Mark as Paid
          </DropdownMenuItem>
        )}
        {canVoidAction && (
          <>
            {(canApproveAction || canMarkPaidAction) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={() => onVoid(bill)}
              className="text-destructive focus:text-destructive"
            >
              <XCircle className="h-4 w-4 mr-2" /> Void Bill
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
const BillsInner = () => {
  const pagination = usePagination();
  const { data: bills, isLoading, isError } = useBills(pagination);
  const { data: companyProfile } = useCompanyProfile();

  const [approveTarget, setApproveTarget] = useState<Bill | null>(null);
  const [paidTarget, setPaidTarget] = useState<Bill | null>(null);
  const [voidTarget, setVoidTarget] = useState<Bill | null>(null);

  const billColumns = useMemo<ColumnDef<Bill, any>[]>(() => [
    { accessorKey: "bill_number", header: "Bill #", cell: ({ getValue }) => <span className="font-mono text-sm">{getValue()}</span> },
    {
      id: "project", header: "Project",
      accessorFn: (row) => row.projects?.name ?? "—",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.projects?.name ?? "—"}
          {row.original.projects?.project_number && <span className="text-muted-foreground ml-1 text-xs">({row.original.projects.project_number})</span>}
        </span>
      ),
    },
    { id: "vendor", header: "Vendor", accessorFn: (row) => row.vendors?.name ?? "—" },
    { accessorKey: "subtotal", header: "Subtotal", cell: ({ getValue }) => <span className="text-right block text-sm">{fmt(getValue())}</span> },
    { accessorKey: "retainage_amount", header: "Retainage", cell: ({ getValue }) => <span className="text-right block text-sm text-muted-foreground">-{fmt(getValue())}</span> },
    { accessorKey: "total", header: "Total", cell: ({ getValue }) => <span className="text-right block font-medium text-sm">{fmt(getValue())}</span> },
    { accessorKey: "bill_date", header: "Date", cell: ({ getValue }) => <span className="text-sm">{getValue() ? format(new Date(getValue()), "MMM d, yyyy") : "—"}</span> },
    { accessorKey: "status", header: "Status", cell: ({ getValue }) => <StatusBadge status={getValue()} /> },
    {
      id: "actions", header: "", enableSorting: false, enableColumnFilter: false, size: 40,
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <BillActions bill={row.original} onApprove={setApproveTarget} onMarkPaid={setPaidTarget} onVoid={setVoidTarget} companyLogoUrl={companyProfile?.logo_url} companyName={companyProfile?.name ?? undefined} />
        </div>
      ),
    },
  ], [companyProfile]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Bills</h1>
        {bills && bills.length > 0 && <ExportToolbar entityType="bills" data={bills} filterDescription="" />}
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All Bills</TabsTrigger>
          <TabsTrigger value="aging">AP Aging</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="pt-2">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Failed to load bills. Please refresh the page.
            </div>
          ) : !bills?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCardIcon className="mx-auto h-8 w-8 mb-2 opacity-30" />
              <p>No bills found.</p>
            </div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="space-y-3 md:hidden">
                {bills.map((b) => (
                  <div key={b.id} className={cn("rounded-lg border bg-card p-4 space-y-2", b.status === "voided" && "opacity-50")}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-medium">{b.bill_number}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={b.status} />
                        <BillActions bill={b} onApprove={setApproveTarget} onMarkPaid={setPaidTarget} onVoid={setVoidTarget} companyLogoUrl={companyProfile?.logo_url} companyName={companyProfile?.name ?? undefined} />
                      </div>
                    </div>
                    <p className="text-sm truncate">{b.projects?.name ?? "—"}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{b.vendors?.name ?? "—"}</span>
                      <span className="font-medium">{fmt(b.total)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop DataTable */}
              <div className="hidden md:block">
                <DataTable
                  tableId="bills-list"
                  columns={billColumns}
                  data={bills}
                  rowClassName={(b) => b.status === "voided" ? "opacity-50" : ""}
                />
              </div>
              <PaginationControls pagination={pagination} />
            </>
          )}
        </TabsContent>

        <TabsContent value="aging" className="pt-2">
          <APAgingReport />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ApproveConfirm
        bill={approveTarget}
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
      />
      <MarkPaidDialog
        bill={paidTarget}
        open={!!paidTarget}
        onClose={() => setPaidTarget(null)}
      />
      <VoidDialog
        bill={voidTarget}
        open={!!voidTarget}
        onClose={() => setVoidTarget(null)}
      />
    </div>
  );
};

// Simple fallback icon inline to avoid import issues
const CreditCardIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M3 10h18M7 15h.01M11 15h2M3 6h18a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V7a1 1 0 011-1z" />
  </svg>
);

const Bills = () => (
  <RequirePermission anyOf={["view_financials", "approve_bills", "view_accounts_payable"]}>
    <BillsInner />
  </RequirePermission>
);

export default Bills;
